// @flow
import React, {PureComponent, useEffect, useState} from 'react';
import {get} from 'lodash';
import ToggleSwitch from 'common/componentsV2/toggleSwitch/ToggleSwitch';
import BaloonMessage from 'common/componentsV2/BaloonMessage';
import Spinner, {SIZES as SPINNER_SIZES} from 'common/componentsV2/Spinner';
import Checkbox from 'common/componentsV2/Checkbox';
import Slider from 'common/componentsV2/Slider';
import {durationScales, resolutionTypes} from 'metrics/services/metricsService';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import TooltipArea from 'common/componentsV2/TooltipArea';
import InputAuto from '../simulationArea/utils/InputAuto';

type PropTypes = {
  title: string,
  subTitle: string,
  absValue: number,
  percentValue: number,
  deltaDurationEnabled: boolean,
  enableAutoFeature: boolean,
  enableAutoBoolVal: boolean,
  enableAutoIsLoading: boolean,
  denyZeroAbsolute: boolean,
  minDurationUI: Object,
  rollup: string,
  deltaDuration: Object,
  onAutoBoolValChange: Function,
  onPercentChange: Function,
  onAbsChange: Function,
  setDeltaDurationEnabled: Function,
  setDeltaDurationNumOfPoints: Function,
  absoluteElement: React.Node,
  percentageElement: React.Node,
  automationId: string,
  enableSpinner: boolean,
  isLoading: boolean,
  tooltipContent: string,
};

const DeltaDurationCondition = ({
  minDuration,
  minDurationUI,
  setDeltaDurationNumOfPoints,
  automationId,
  deltaDuration,
  rollup,
}: {
  minDuration: Object,
  minDurationUI: Object,
  setDeltaDurationNumOfPoints: Function,
  automationId: string,
  deltaDuration: number,
  rollup: string,
}) => {
  const [localValue, setLocalValue] = useState(minDuration);
  const deltaDurationValue = get(deltaDuration, 'minDuration', '');

  useEffect(() => {
    setLocalValue(deltaDurationValue);
  }, [deltaDurationValue]);

  const secondsPerUnit = durationScales[resolutionTypes[rollup].units].seconds;

  const numOfPointsFormatted = parseFloat(deltaDurationValue / durationScales[resolutionTypes[rollup].units].seconds);

  const deltaDurationUnits = resolutionTypes[rollup].units;
  const rollupBySec = resolutionTypes[rollup].ms / 1000;

  return (
    <React.Fragment>
      <Box mb={1.5}>Alert will be triggered if delta lasts at least:</Box>
      <Box display="flex">
        <Box flexShrink={0}>
          <Box width={67} height={30} mr={2}>
            <InputAuto
              fullSize
              automationId={`${automationId}_volumeValue`}
              disabled
              type="number"
              name="volume-value"
              value={numOfPointsFormatted}
              onChange={(event) => {
                setDeltaDurationNumOfPoints(event.target.value * secondsPerUnit);
              }}
              delay={1000}
              additionalProps={{
                min: parseFloat(rollupBySec / secondsPerUnit),
                max: parseFloat(minDurationUI.value / secondsPerUnit),
              }}
            />
          </Box>
          <Box mb={1.5} mt={0.5} css={{textTransform: 'capitalize'}}>
            {deltaDurationUnits}
          </Box>
        </Box>
        <Slider
          automationId={`${automationId}_deltaDurationSlider`}
          value={localValue}
          onChange={setLocalValue}
          onRelease={setDeltaDurationNumOfPoints}
          min={rollupBySec}
          max={minDurationUI.value}
          step={rollupBySec}
          tooltipValueFormatter={(value) => parseFloat(value / secondsPerUnit)}
        />
      </Box>
    </React.Fragment>
  );
};

const absoluteInputAdditionalProps = {
  min: 0,
};

const percentageInputAdditionalProps = {
  min: 0,
  max: 1000,
};

class AbsPercentageConditionTemplate extends PureComponent {
  props: PropTypes;

  state = {
    isDeltaDurationActive: false,
  };

  componentDidUpdate() {
    if (this.props.deltaDurationEnabled) {
      this.setIsActive();
    }
  }

  autoCheckedChanged = (value) => {
    const {onAutoBoolValChange} = this.props;
    onAutoBoolValChange(!value);
  };

  onPercentChange = (e) => {
    this.props.onPercentChange(e.target.value);
  };

  onAbsChange = (e) => {
    this.props.onAbsChange(e.target.value);
  };

  setDeltaDurationEnabled = (isDeltaDurationEnabled) => {
    this.props.setDeltaDurationEnabled(!isDeltaDurationEnabled);
  };

  setDeltaDurationNumOfPoints = (value) => {
    const {setDeltaDurationNumOfPoints, rollup} = this.props;
    setDeltaDurationNumOfPoints({value, rollup});
  };

  getUnitsByRollup = () => resolutionTypes[this.props.rollup].units;

  getRollupBySecond = () => resolutionTypes[this.props.rollup].ms / 1000;

  formatDuration = () => {
    const {deltaDuration, rollup} = this.props;
    const deltaDurationValue = get(deltaDuration, 'minDuration', 0);

    return Math.ceil(deltaDurationValue / durationScales[resolutionTypes[rollup].units].seconds);
  };

  deltaDurationInactiveMsgRendered = () => {
    const numOfPointsFormatted = this.formatDuration();
    let deltaDurationUnits = this.getUnitsByRollup();
    if (numOfPointsFormatted === 1 && deltaDurationUnits.slice(-1) === 's') {
      deltaDurationUnits = deltaDurationUnits.slice(0, deltaDurationUnits.length - 1);
    }
    return (
      <Box my={1}>
        <BaloonMessage>
          In order to use this condition, the Minimum Duration for the anomaly needs to be larger than{' '}
          <span>
            {numOfPointsFormatted} {deltaDurationUnits}
          </span>
        </BaloonMessage>
      </Box>
    );
  };

  setIsActive = () => {
    const {minDurationUI} = this.props;
    const rollupBySec = this.getRollupBySecond();
    this.setState({isDeltaDurationActive: minDurationUI && minDurationUI.value > rollupBySec});
  };

  render() {
    const {
      absValue,
      percentValue,
      minDurationUI,
      deltaDuration,
      deltaDurationEnabled,
      title,
      subTitle,
      absoluteElement,
      percentageElement,
      automationId,
      enableSpinner,
      isLoading,
      denyZeroAbsolute,
      tooltipContent,
    } = this.props;
    const {isDeltaDurationActive} = this.state;

    const {enabled, minDuration} = deltaDuration || {};
    const safeAutoBoolVal = !!this.props.enableAutoBoolVal;

    return (
      <React.Fragment>
        <TooltipArea automationId={`${automationId}_absPercentageContainer`} text={tooltipContent}>
          {(info) => (
            <React.Fragment>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <div className="text16med lineHeight_16">
                  {title}
                  {info}
                </div>
                {this.props.enableAutoFeature && (
                  <Box className="cb2 analytics" position="relative" display="flex" flexShrink={0}>
                    {this.props.enableAutoIsLoading && (
                      <Box right={145} bottom={7} position="absolute">
                        <Spinner color="#fff" size={SPINNER_SIZES.SMALL_30} />
                      </Box>
                    )}
                    <Box mr={1.5} color={safeAutoBoolVal ? 'purple.600' : 'gray.400'} fontWeight="bold" fontSize={16}>
                      Automatic
                    </Box>
                    <ToggleSwitch
                      automationId={`${automationId}_automaticSwitch`}
                      theme="purple"
                      isChecked={safeAutoBoolVal}
                      onToggle={() => this.autoCheckedChanged(safeAutoBoolVal)}
                    />
                  </Box>
                )}
              </Box>
              <div className="text16reg lineHeight_16">{subTitle}</div>
              <Grid container automation-id="alertSettingsPercentagePane" spacing={2}>
                <Grid item>
                  <Box width={124} height={36} mb={0.5}>
                    <InputAuto
                      allowEmpty
                      denyZero={denyZeroAbsolute}
                      automationId={`${automationId}_absoluteInput`}
                      fullSize
                      type="number"
                      className="dark-input"
                      placeholder="Absolute"
                      isDisabled={safeAutoBoolVal}
                      step={safeAutoBoolVal ? '0.1' : '1'}
                      value={absValue || ''}
                      delay={1000}
                      onChange={this.onAbsChange}
                      additionalProps={absoluteInputAdditionalProps}
                    />
                  </Box>
                  {absoluteElement}
                </Grid>
                <Grid item>
                  <Box width={124} height={36} mb={0.5}>
                    <InputAuto
                      allowEmpty
                      automationId={`${automationId}_percentageInput`}
                      fullSize
                      type="number"
                      className="dark-input"
                      placeholder="Percentage"
                      additionalProps={percentageInputAdditionalProps}
                      isDisabled={safeAutoBoolVal}
                      value={percentValue || ''}
                      delay={1000}
                      onChange={this.onPercentChange}
                    />
                  </Box>
                  {percentageElement}
                </Grid>
                {enableSpinner && isLoading && (
                  <Grid item>
                    <Box display="flex" justifyContent="center" alignItems="center" width={1} height={36}>
                      <Box className="icon ion-load-c spin" fontSize={24} color="gray.400" />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </React.Fragment>
          )}
        </TooltipArea>
        {deltaDurationEnabled && (
          <TooltipArea
            automationId={`${automationId}_deltaDurationContainer`}
            text="Set the minimum duration the anomaly should surpass the delta set above in order to trigger an alert"
          >
            {(info) => (
              <React.Fragment>
                <Box mb={1} mt={3} display="flex">
                  <Checkbox
                    isDisabled={!isDeltaDurationActive}
                    isChecked={enabled}
                    onChange={() => this.setDeltaDurationEnabled(enabled)}
                  >
                    <div className="text16med lineHeight_16 ml_0-5 color_inherit">Override Delta Duration</div>
                  </Checkbox>
                  {info}
                </Box>
                {!isDeltaDurationActive && this.deltaDurationInactiveMsgRendered()}
                {enabled && isDeltaDurationActive && (
                  <DeltaDurationCondition
                    minDuration={minDuration}
                    minDurationUI={minDurationUI}
                    deltaDuration={deltaDuration}
                    rollup={this.props.rollup}
                    setDeltaDurationNumOfPoints={(value) => this.setDeltaDurationNumOfPoints(value)}
                  />
                )}
              </React.Fragment>
            )}
          </TooltipArea>
        )}
      </React.Fragment>
    );
  }
}

export default AbsPercentageConditionTemplate;
